import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useT } from "../i18n";

export default function SearchBar({ collapsible = false }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(!collapsible);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        if (collapsible && !q) setExpanded(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [collapsible, q]);

  const openCollapsed = () => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (collapsible && !expanded) {
    return (
      <button
        onClick={openCollapsed}
        className="w-8 h-8 rounded-[4px] flex items-center justify-center bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[var(--tf-primary)]/50 text-zinc-400 hover:text-white transition-all duration-200 hover:scale-105"
        title="Caută"
        data-testid="search-toggle"
      >
        <Search className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <div className={`relative ${collapsible ? "w-72" : "w-full max-w-sm"}`} ref={ref} data-testid="search-bar">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t("header.search_placeholder")}
          className="tf-input pl-9 pr-9 py-2 text-xs w-full"
          data-testid="search-input"
        />
        {collapsible && (
          <button
            onClick={() => { setQ(""); setOpen(false); setExpanded(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-zinc-500 hover:text-white"
            data-testid="search-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && q.length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 tf-surface-elev tf-clip-card z-50 max-h-96 overflow-y-auto shadow-2xl" data-testid="search-results">
          {loading && <div className="p-4 text-xs tf-mono text-zinc-500">Searching...</div>}
          {results && (
            <>
              {(results.topics || []).length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] tf-section-title border-b border-white/5">Topics</div>
                  {results.topics.slice(0, 5).map((t) => (
                    <Link key={t.id} to={`/topic/${t.id}`} onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-zinc-200 hover:bg-white/5">
                      <div className="font-semibold truncate">{t.title}</div>
                      <div className="text-[10px] text-zinc-500">{t.board_name}</div>
                    </Link>
                  ))}
                </div>
              )}
              {(results.users || []).length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] tf-section-title border-b border-white/5">Users</div>
                  {results.users.slice(0, 5).map((u) => (
                    <Link key={u.id} to={`/u/${u.username}`} onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-zinc-200 hover:bg-white/5">
                      {u.username}
                    </Link>
                  ))}
                </div>
              )}
              {(results.posts || []).length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] tf-section-title border-b border-white/5">Posts</div>
                  {results.posts.slice(0, 5).map((p) => (
                    <Link key={p.id} to={`/topic/${p.topic_id}`} onClick={() => setOpen(false)} className="block px-4 py-2 text-xs text-zinc-300 hover:bg-white/5 line-clamp-2">
                      {p.content}
                    </Link>
                  ))}
                </div>
              )}
              {!results.topics?.length && !results.users?.length && !results.posts?.length && (
                <div className="p-4 text-xs tf-mono text-zinc-500">No results</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
